import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
    setCommandMessage,
    resetOperation
} from '../../../redux/operationSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { COMMANDS } from '../../../utils/commonProperties';
import { createDrawingClickHandler } from "./useDrawingEvents";
import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { createEscapeHandler } from '../../../utils/escapeHandler';
import { DrawCommand } from '../../../modules/undoRedo';
import { addUserEntity } from '../../../redux/dxfSlice';

const useDrawSpline = (scene, camera, renderer, snapPoints) => {
    const pointsRef = useRef([]);
    const curveObjectRef = useRef(null);
    const snapMarkerRef = useRef(null);
    const dispatch = useDispatch();

    const { commandType } = useSelector(state => state.operation);
    const gridMode = useSelector(state => state.mods.gridMode);
    const objectSnap = useSelector(state => state.mods.objectSnap);
    const snapMode = useSelector((state) => state.mods.snapMode);
    const { activeLayerName } = useSelector((state) => state.layer);
    const activeTabId = useSelector(state => state.projectTabs.activeTabId);

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_SPLINE || !scene || !camera || !renderer) return;

        const domElement = renderer.domElement;
        dispatch(setCommandMessage("Spline için noktaları seçin. Bitirmek için Enter, iptal için ESC."));

        const handleSplinePoint = (point) => {
            if (!(point instanceof THREE.Vector3)) {
                console.warn("Geçersiz snap noktası:", point);
                return;
            }

            pointsRef.current.push(point);

            if (pointsRef.current.length < 2) return;

            if (curveObjectRef.current) {
                scene.remove(curveObjectRef.current);
                curveObjectRef.current.geometry.dispose();
                curveObjectRef.current.material.dispose();
                curveObjectRef.current = null;
            }

            const curve = new THREE.CatmullRomCurve3(pointsRef.current);
            const curvePoints = curve.getPoints(50);
            const geometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
            const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
            const curveObject = new THREE.Line(geometry, material);
            scene.add(curveObject);
            curveObjectRef.current = curveObject;
        };

        const handleClick = createDrawingClickHandler({
            scene,
            camera,
            renderer,
            snapPoints,
            snapMarkerRef,
            gridMode,
            objectSnap,
            snapMode,
            onPointSelected: handleSplinePoint
        });

        const handleMouseMove = (e) => {
            const rect = domElement.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            const { finalPoint, snapped, snapSource } = getSnappedPoint(mouseNDC, worldPoint, snapPoints, camera, renderer, { snapMode: snapMode });

            //   if (!finalPoint || !(finalPoint instanceof THREE.Vector3)) return;

            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(snapSource);
                marker.position.copy(finalPoint);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            if (pointsRef.current.length === 0) return;

            const previewPoints = [...pointsRef.current, finalPoint];
            const curve = new THREE.CatmullRomCurve3(previewPoints);
            const curvePoints = curve.getPoints(50);
            const geometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
            const material = new THREE.LineBasicMaterial({ color: 0xffff00 });

            if (curveObjectRef.current) {
                scene.remove(curveObjectRef.current);
                curveObjectRef.current.geometry.dispose();
                curveObjectRef.current.material.dispose();
            }

            const curveObject = new THREE.Line(geometry, material);
            scene.add(curveObject);
            curveObjectRef.current = curveObject;
        };

        const escapeHandler = createEscapeHandler({
            scene,
            dispatch,
            tempObjectRef: curveObjectRef,
            snapMarkerRef,
            additionalCleanup: () => {
                pointsRef.current = [];
                dispatch(setCommandMessage(""));
            }
        });

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                escapeHandler(e);
            } else if (e.key === 'Enter' && pointsRef.current.length >= 2) {
                const curve = new THREE.CatmullRomCurve3(pointsRef.current);
                const curvePoints = curve.getPoints(50);
                const geometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
                const material = new THREE.LineBasicMaterial({ color: 0xffffff });
                const curveLine = new THREE.Line(geometry, material);
                curveLine.userData = {
                    id: generateUniqueId(),
                    isSelectable: true,
                    type: "spline",
                    splinePoints: curvePoints.map(p => ({ x: p.x, y: p.y, z: p.z })),
                };
                scene.add(curveLine);

                // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
                dispatch(addUserEntity({
                    type: 'SPLINE',
                    fitPoints: pointsRef.current.map(p => ({ x: p.x, y: p.y, z: p.z || 0 })),
                    degreeOfSplineCurve: 3,
                    layer: activeLayerName || '0',
                    _userEntityId: curveLine.userData.id,
                    _userDrawn: true,
                    _tabId: activeTabId || 'mimari',
                }));

                // Undo-redo kaydı
                const manager = window.undoRedoManager;
                if (manager) {
                    const tx = manager.beginTransaction('Draw Spline');
                    manager.addCommand(new DrawCommand(curveLine));
                    manager.commitTransaction(tx);
                }

                if (curveObjectRef.current) {
                    scene.remove(curveObjectRef.current);
                    curveObjectRef.current.geometry.dispose();
                    curveObjectRef.current.material.dispose();
                    curveObjectRef.current = null;
                }

                pointsRef.current = [];
                dispatch(resetOperation());
                dispatch(setCommandMessage(""));
            }
        };

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [commandType, scene, camera, renderer, gridMode, objectSnap, snapPoints, dispatch, snapMode]);
};

export default useDrawSpline;